import { createSignal, createMemo, onMount, For, Show } from 'solid-js';
import { useSFetch } from '../hooks/sfetch';
import { useToast } from '../components/MessageBox';
import { useTranslation } from '../hooks/useTranslation';
import { SearchInput } from '../components/SearchInput';
import { showConfirm } from '../components/Dialog';
import Text from '../components/Text';
import { formatDateTime } from '../services/dateFormat';
import styles from './UsernameTracker.module.css';

interface UsernameChange {
    oldUsername: string;
    newUsername: string;
    detectedAt: string;
}

interface TrackedProfile {
    id: string;
    uuid: string;
    currentUsername: string;
    firstTrackedAt: string;
    lastCheckedAt: string | null;
    changes: UsernameChange[];
}

export default function UsernameTracker() {
    const sfetch = useSFetch();
    const toast = useToast();
    const { t } = useTranslation();

    const [profiles, setProfiles] = createSignal<TrackedProfile[]>([]);
    const [loading, setLoading] = createSignal(true);
    const [search, setSearch] = createSignal('');
    const [addUsername, setAddUsername] = createSignal('');
    const [addLoading, setAddLoading] = createSignal(false);
    const [removingId, setRemovingId] = createSignal<string | null>(null);
    const [expandedId, setExpandedId] = createSignal<string | null>(null);

    const load = async () => {
        const res = await sfetch('/api/username-tracker');
        if (res.ok) setProfiles((res.value?.profiles ?? []) as TrackedProfile[]);
        setLoading(false);
    };

    onMount(load);

    const filtered = createMemo(() => {
        const q = search().trim().toLowerCase();
        if (!q) return profiles();
        return profiles().filter(p =>
            p.currentUsername.toLowerCase().includes(q) || p.uuid.toLowerCase().includes(q));
    });

    const addUsernameTrack = async (e: Event) => {
        e.preventDefault();
        const name = addUsername().trim();
        if (!name) return;
        setAddLoading(true);
        const res = await sfetch('/api/username-tracker', {
            method: 'POST',
            body: JSON.stringify({ username: name }),
        });
        setAddLoading(false);
        if (res.ok) {
            toast?.info(t('USERNAME_TRACKER_ADDED'));
            setAddUsername('');
            await load();
        }
    };

    const removeProfile = async (profile: TrackedProfile) => {
        if (!await showConfirm(t('USERNAME_TRACKER_REMOVE_CONFIRM', profile.currentUsername), true)) return;
        setRemovingId(profile.id);
        const res = await sfetch(`/api/username-tracker/${profile.id}`, { method: 'DELETE' });
        setRemovingId(null);
        if (res.ok) {
            toast?.info(t('USERNAME_TRACKER_REMOVED'));
            await load();
        }
    };

    const toggleExpand = (id: string) => setExpandedId(prev => prev === id ? null : id);

    return (
        <div class={styles.container}>
            <div class={styles.headerRow}>
                <h1>
                    <i class="fa-solid fa-user-clock" />
                    <Text val="USERNAME_TRACKER_TITLE" />
                </h1>
                <Show when={!loading()}>
                    <span class={styles.count}>
                        {profiles().length}&nbsp;<Text val="USERNAME_TRACKER_COUNT" />
                    </span>
                </Show>
            </div>

            <form class="fcard" classList={{ [styles.addForm]: true }} onSubmit={addUsernameTrack}>
                <label class={styles.field}>
                    <span><Text val="USERNAME_TRACKER_ADD_LABEL" /></span>
                    <input
                        type="text"
                        placeholder={t('USERNAME_TRACKER_ADD_PLACEHOLDER')}
                        value={addUsername()}
                        onInput={(e) => setAddUsername(e.currentTarget.value)}
                    />
                </label>
                <button type="submit" class="btn btn-primary" disabled={addLoading() || !addUsername().trim()}>
                    <Show when={addLoading()} fallback={<><i class="fa-solid fa-plus" /> <Text val="USERNAME_TRACKER_ADD_BUTTON" /></>}>
                        <i class="fa-solid fa-spinner fa-spin" /> <Text val="USERNAME_TRACKER_ADD_BUTTON" />
                    </Show>
                </button>
            </form>

            <Show when={!loading() && profiles().length > 0}>
                <SearchInput
                    value={search()}
                    onInput={setSearch}
                    placeholder={t('USERNAME_TRACKER_SEARCH_PLACEHOLDER')}
                />
            </Show>

            <Show when={!loading()} fallback={
                <div class={styles.list}>
                    <For each={[0, 1, 2]}>{() => <div class={styles.skeletonCard} />}</For>
                </div>
            }>
                <Show when={filtered().length > 0} fallback={
                    <div class="fcard">
                        <div class={styles.empty}>
                            <i class="fa-solid fa-user-clock" />
                            <p>
                                <Text val={profiles().length === 0 ? 'USERNAME_TRACKER_EMPTY' : 'USERNAME_TRACKER_EMPTY_SEARCH'} />
                            </p>
                        </div>
                    </div>
                }>
                    <div class={styles.list}>
                        <For each={filtered()}>{(profile) => (
                            <div class="fcard v">
                                <div class={styles.row}>
                                    <img
                                        class={styles.avatar}
                                        src={`https://mc-heads.net/avatar/${profile.uuid}/40`}
                                        alt={profile.currentUsername}
                                    />
                                    <div class={styles.identity}>
                                        <div class={styles.username}>{profile.currentUsername}</div>
                                        <div class={styles.uuid}>{profile.uuid}</div>
                                    </div>

                                    <Show
                                        when={profile.changes.length > 0}
                                        fallback={<span class={styles.changeBadgeEmpty}><Text val="USERNAME_TRACKER_NO_CHANGES" /></span>}
                                    >
                                        <span class={styles.changeBadge} onClick={() => toggleExpand(profile.id)}>
                                            {t('USERNAME_TRACKER_CHANGES_COUNT', String(profile.changes.length))}
                                        </span>
                                    </Show>

                                    <div class={styles.meta}>
                                        <span>{t('USERNAME_TRACKER_TRACKED_SINCE', formatDateTime(profile.firstTrackedAt))}</span>
                                        <span>
                                            {t('USERNAME_TRACKER_LAST_CHECKED', profile.lastCheckedAt
                                                ? formatDateTime(profile.lastCheckedAt)
                                                : t('USERNAME_TRACKER_NEVER_CHECKED'))}
                                        </span>
                                    </div>

                                    <button
                                        class="btn btn-danger btn-icon"
                                        title={t('USERNAME_TRACKER_REMOVE_BUTTON')}
                                        disabled={removingId() === profile.id}
                                        onClick={() => removeProfile(profile)}
                                    >
                                        <i class="fa-solid fa-trash" />
                                    </button>
                                </div>

                                <Show when={expandedId() === profile.id && profile.changes.length > 0}>
                                    <div class={styles.history}>
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th><Text val="USERNAME_TRACKER_COL_OLD" /></th>
                                                    <th><Text val="USERNAME_TRACKER_COL_NEW" /></th>
                                                    <th><Text val="USERNAME_TRACKER_COL_DETECTED" /></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <For each={profile.changes}>{(change) => (
                                                    <tr>
                                                        <td class={styles.oldName}>{change.oldUsername}</td>
                                                        <td class={styles.newName}>{change.newUsername}</td>
                                                        <td>{formatDateTime(change.detectedAt)}</td>
                                                    </tr>
                                                )}</For>
                                            </tbody>
                                        </table>
                                    </div>
                                </Show>
                            </div>
                        )}</For>
                    </div>
                </Show>
            </Show>
        </div>
    );
}
